// libraries
import React from 'react';
import { FormRenderProps, FormSpy } from 'react-final-form';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// custom components
import Form from 'components/Form';
import ClientSignUpSuccess from './ClientSignUpSuccess';
import Select from 'components/Form/Select/Select';
import TermsAcceptedCheckbox from 'modules/SignUpWithFooterContactForms/components/TermsAcceptedCheckbox';

// styled components
import {
  SelectStyles,
  FullWidthSelectStyles,
  Heading,
  InputsWrapper,
  PairedInputs,
  StyledInput,
  SelectPlaceholder,
} from '../../sharedStyles';
import { ColumnedFlex, Flex, Error, PrimaryButton } from 'shared/styledComponents';

import devices from 'theme/devices';

// utils
import { getValue } from 'utils/getValue';
import { validationMessages } from 'modules/LogInOrCreateAccount/CreateAccount/types';

const Wrapper = styled(Flex)`
  width: 50%;
  justify-content: center;
  padding: 90px 0 65px;
  background-color: ${({ theme: { colors } }): string => colors.bismark};

  @media ${devices.mobile} {
    width: 100%;
    justify-content: center;
  }
`;

const FormWrapper = styled(ColumnedFlex)`
  /* width: 100%; */
  max-width: 570px;

  @media ${devices.mobile} {
    width: fit-content;
    max-width: 310px;
  }
`;

const FormContentWrapper = styled(ColumnedFlex)`
  justify-content: center;
  align-items: flex-start;
  min-height: 365px;
`;

const StyledSubmitButton = styled(PrimaryButton)(
  ({ isLoading, theme: { colors } }) => `
    width: 100px;
    background-color: ${colors.blueBayoux};
    cursor: ${isLoading ? 'wait' : 'pointer'};
  `,
);

const PrivacyText = styled.p(
  ({ theme: { colors, fonts } }) => `
    font-family: ${fonts.canelaThin};
    color: ${colors.primary};
    font-size: 12px;
    line-height: 16px;
    color: #fff;
    margin-top: 7px;
  `,
);

const PrivacyTextLink = styled(PrivacyText)`
  text-decoration: underline;
  cursor: pointer;
`;

interface ClientSignUpFormProps {
  stageOptions?: any;
  serviceOptions?: any;
  onSubmit: (values: any) => void;
  formValidation: (values: any) => any;
  handleAnalytics: (values: any) => any;
}

const ClientSignUpForm = ({
  stageOptions,
  serviceOptions,
  onSubmit,
  formValidation,
  handleAnalytics,
}: ClientSignUpFormProps): JSX.Element => (
  <Wrapper>
    <FormWrapper>
      <Form onSubmit={onSubmit} validate={formValidation}>
        {({
          form: { reset },
          submitting,
          submitSucceeded,
          submitFailed,
          submitErrors,
          hasValidationErrors,
          errors,
        }: FormRenderProps): JSX.Element => (
          <FormContentWrapper>
            <FormSpy
              subscription={{ dirtyFields: true }}
              onChange={(props) => {
                handleAnalytics(props);
              }}
            />
            {submitSucceeded ? (
              <ClientSignUpSuccess onClose={reset} />
            ) : (
              <>
                <Heading>Sign up to receive the latest in parental wellness.</Heading>
                <InputsWrapper>
                  <PairedInputs>
                    <StyledInput name="firstName" type="text" placeholder="First Name" size="small" />
                    <StyledInput name="lastName" type="text" placeholder="Last Name" size="small" />
                  </PairedInputs>
                  <PairedInputs>
                    <StyledInput name="email" type="email" placeholder="Email Address" size="small" />
                    <Select
                      name="stage"
                      options={stageOptions}
                      customStyles={SelectStyles}
                      placeholder={<SelectPlaceholder>Stage</SelectPlaceholder>}
                    />
                  </PairedInputs>
                  <Select
                    name="interests"
                    options={serviceOptions}
                    customStyles={FullWidthSelectStyles}
                    isMulti
                    placeholder={<SelectPlaceholder>Interested in... (multiple)</SelectPlaceholder>}
                  />
                  <PrivacyText>
                    By providing your email address you agree to our&nbsp;
                    <PrivacyTextLink as={Link} to="/privacy-policy" target="_blank">
                      Privacy Policy
                    </PrivacyTextLink>
                    &nbsp;and&nbsp;
                    <PrivacyTextLink as={Link} to="/terms-of-use" target="_blank">
                      Terms of Service.
                    </PrivacyTextLink>
                  </PrivacyText>
                </InputsWrapper>
                {submitFailed && hasValidationErrors && (
                  <Error size="small">
                    {!!Object.keys(errors).length &&
                      `${getValue(Object.keys(errors)[0], validationMessages)} ${errors[Object.keys(errors)[0]]}`}
                  </Error>
                )}
                {submitFailed && submitErrors?.error && <Error size="small">{submitErrors?.error}</Error>}
                <StyledSubmitButton type="submit" size="small" isLoading={submitting}>
                  Submit
                </StyledSubmitButton>
              </>
            )}
          </FormContentWrapper>
        )}
      </Form>
    </FormWrapper>
  </Wrapper>
);

export default ClientSignUpForm;
